import { useQuery } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'

// ─── Backend response types ────────────────────────────────────────


export interface ManifestSubsection {
  id: string
  text: string
  url: string
}

export interface ManifestItem {
  slug: string
  label: string
  url: string
  tool_count: number | null
  subsections: ManifestSubsection[]
}

export type Audience = 'user' | 'admin'

export interface ManifestSection {
  id: string
  label: string
  /**
   * 'user' = visible to every authenticated role (operator how-to
   * content). 'admin' = developer/CARLA reference, hidden from
   * non-admin operators by the HelpSidebar audience filter.
   */
  audience: Audience
  items: ManifestItem[]
}

export interface HelpManifest {
  sections: ManifestSection[]
  page_count: number
}

export interface PageHeading {
  id: string
  text: string
  level: number
}

export interface PageBreadcrumb {
  label: string
  url: string
}

export interface HelpPage {
  section: string
  slug: string
  title: string
  html: string
  headings: PageHeading[]
  breadcrumbs: PageBreadcrumb[]
  github_url: string
  mtime: number
}

export interface SearchHit {
  section: string
  slug: string
  title: string
  snippet: string
  url: string
  score: number
}

export interface HelpSearchResponse {
  query: string
  hits: SearchHit[]
}


// ─── Hooks ─────────────────────────────────────────────────────────


/**
 * Manifest is small (~5 KB) + the doc tree changes on deploy only —
 * cache aggressively so the sidebar tree doesn't re-fetch on every
 * page navigation within /help.
 */
export function useHelpManifest() {
  return useQuery({
    queryKey: ['help', 'manifest'],
    queryFn: () => jsonFetch<HelpManifest>('/api/help/manifest'),
    staleTime: 60 * 60 * 1000,  // 1 hour
    gcTime: 24 * 60 * 60 * 1000, // keep 24h
  })
}


/**
 * Per-page fetch. Keyed by section + slug + optional sub-slug so
 * /help/modules/contacts and /help/modules/contacts/tools cache
 * separately.
 */
export function useHelpPage(
  section: string | undefined,
  slug: string | undefined,
  subSlug?: string,
) {
  const path = subSlug
    ? `/api/help/page/${encodeURIComponent(section ?? '')}/${encodeURIComponent(slug ?? '')}/${encodeURIComponent(subSlug)}`
    : `/api/help/page/${encodeURIComponent(section ?? '')}/${encodeURIComponent(slug ?? '')}`
  return useQuery({
    queryKey: ['help', 'page', section, slug, subSlug],
    queryFn: () => jsonFetch<HelpPage>(path),
    enabled: Boolean(section && slug),
    staleTime: 5 * 60 * 1000,  // 5 min — docs change on deploy
  })
}


/** Fire-and-forget search. Caller debounces. */
export function useHelpSearch(query: string, enabled: boolean) {
  const trimmed = query.trim()
  return useQuery({
    queryKey: ['help', 'search', trimmed],
    queryFn: () =>
      jsonFetch<HelpSearchResponse>(
        `/api/help/search?q=${encodeURIComponent(trimmed)}`,
      ),
    enabled: enabled && trimmed.length >= 2,
    staleTime: 5 * 60 * 1000,
  })
}
